import CustomSelect from "components/custom-select/CustomSelect";
import DropdownWithSearch from "components/dropdown-with-search";
import { getDisplayNameOptions } from "components/provider-intake/utility/getDisplayNameOptions";
import {
  ImageUpload,
  makeImageFromUri,
  resizeImageAsBlob,
  resizeImageAsDataUri,
} from "components/utility/image-upload";
import { get, set } from "idb-keyval";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tab,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { LANGUAGE_SPOKEN_OPTIONS } from "../utility/constants";
// import {
//   getOrchidProNameAndAllGraduateDegreesString,
//   getOrchidProSpecialtyStringFromNewArr,
//   getOrchidProTypeStringFromNewArr,
// } from "utils/formatter/orchidPro";
import { ReactComponent as AIEmoji16 } from "assets/svg/ai-emoji-16.svg";
import { ReactComponent as OutlineInfoCircle16 } from "assets/svg/outline-info-circle-16.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import putOrchidProProfileImg from "services/putOrchidProProfileImg";
import { apiHostAndPath } from "utils/api";
import getOrchidProDisplayName from "utils/getOrchidProDisplayName";
import getOrchidProMedicalDegree from "utils/getOrchidProMedicalDegree";
// hooks
import { useUser } from "contexts/UserContext";
import deleteOrchidProProfileImg from "services/deleteOrchidProProfileImg";
import { debouncedTrack, track } from "utils/amplitude";

import getClasses from "utils/getClasses";
import styles from "./AboutMe.module.scss";

const getCurrLinks = (links) => {
  if (links?.length > 0) {
    return links;
  } else {
    return [""];
  }
};

const AboutMe = ({
  data,
  registration,
  fieldChangedCallback,
  amplitudePageName,
  getAIBio,
  gptBusy,
}) => {
  const { user, updateUserInfo } = useUser();
  const [tab, setTab] = useState("name");

  const [imgUpdateTimestamp, setImgUpdateTimestamp] = useState(Date.now());
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  useEffect(() => {
    if (registration) {
      get("imagePreviewUrl").then((val) => {
        return setImagePreviewUrl(typeof val === "string" ? val : "");
      });
    } else {
      setImagePreviewUrl(
        user
          ? apiHostAndPath(
              `/api/users/orchid_pro/profile_image/${user.sub}?timestamp=${imgUpdateTimestamp}`,
            )
          : "",
      );
    }
  }, [imgUpdateTimestamp, registration, user]);

  const onInputChange = (event) => {
    const inputField = event.currentTarget;
    fieldChangedCallback(inputField.name, inputField.value);
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(e);

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  };

  const onLinkInputChange = (index) => (event) => {
    const { links } = data;
    const inputField = event.currentTarget;
    // const value = event.target.value;
    links[index] = inputField.value;
    fieldChangedCallback("links", links);
  };

  const addLink = (event) => {
    const currLinks = getCurrLinks(data.links);
    const inputField = event.currentTarget;
    const { value } = inputField;
    fieldChangedCallback("links", [...currLinks, value]);
  };

  const onImageChanged = useCallback(
    async (imagePreviewUrl: string | ArrayBuffer | null | undefined) => {
      try {
        // delete
        if (!imagePreviewUrl) {
          if (registration) {
            setImagePreviewUrl("");
            set("imagePreviewUrl", "");
          } else {
            await deleteOrchidProProfileImg();
            setImagePreviewUrl("");
            setImgUpdateTimestamp(Date.now());
            await updateUserInfo();
          }
          return;
        }

        // create
        const img = await makeImageFromUri(imagePreviewUrl);
        if (registration) {
          const uri = resizeImageAsDataUri(img, 800, 800);
          setImagePreviewUrl(uri);
          set("imagePreviewUrl", uri);
        } else {
          const blob = await resizeImageAsBlob(img, 800, 800);
          await putOrchidProProfileImg(blob);
          setImgUpdateTimestamp(Date.now());
          await updateUserInfo();
        }
      } catch (err) {
        console.error("failed to set profile image", err);
      }
    },
    [registration, updateUserInfo],
  );

  return (
    <>
      {registration && (
        <Tabs
          activeKey={tab}
          onSelect={(s) => setTab(s || "name")}
          className={styles["about-me__tabs"]}
        >
          <Tab eventKey="name" title="Name"></Tab>
          <Tab eventKey="profile" title="Profile"></Tab>
        </Tabs>
      )}

      {(!registration || (registration && tab === "profile")) && (
        <>
          {/* {!registration ? (
            <div
              className={getClasses(
                styles["intake-form__title"],
                styles["intake-form__title--edit-profile"],
              )}
            >
              Profile picture
            </div>
          ) : (
            <div className={styles["intake-form__title"]}>
              2. Set your profile.
            </div>
          )} */}
          <Row>
            <Col
              className={!registration ? "d-flex pl-0 pr-2" : "p-0"}
              md={!registration ? 6 : 12}
            >
              <div className={styles["edit-profile__about"]}>
                <div
                  className={getClasses(
                    styles["intake-form__title"],
                    !registration &&
                      styles["intake-form__sub-title--edit-profile"],
                  )}
                >
                  Profile Picture
                </div>
                {!registration ? (
                  <p
                    className={getClasses(
                      styles["intake-form__text"],
                      styles["intake-form__text-block"],
                    )}
                  >
                    Upload your profile picture—it's the face of your practice
                    for current and future clients.
                  </p>
                ) : (
                  <p className={styles["intake-form__text"]}>
                    Add a professional headshot of you. It will be presented on
                    the profile card and the professional directory.
                  </p>
                )}

                <Card className={styles["profile-pic__card"]}>
                  <Card.Body className={styles["profile-pic__card-body"]}>
                    <ImageUpload
                      imagePreviewUrl={imagePreviewUrl}
                      onImageChanged={(nextImagePreviewUrl) =>
                        onImageChanged(nextImagePreviewUrl)
                      }
                      amplitudePageName={amplitudePageName}
                    />
                    {/* <Card.Text
                      as="h4"
                      className={styles["profile-pic__card-pro-name"]}
                    >
                      {getOrchidProNameAndAllGraduateDegreesString(data)}
                    </Card.Text>

                    <Card.Text className={styles["profile-pic__card-pro-type"]}>
                      {getOrchidProTypeStringFromNewArr(data)}
                    </Card.Text>

                    <Card.Text
                      className={styles["profile-pic__card-certificate"]}
                    >
                      {getOrchidProSpecialtyStringFromNewArr(data)}
                    </Card.Text> */}
                  </Card.Body>
                </Card>
              </div>
            </Col>
            <Col
              className={!registration ? "d-flex pl-3 pr-0" : "p-0"}
              md={!registration ? 6 : 12}
            >
              <div className={styles["edit-profile__about"]}>
                <div
                  className={getClasses(
                    styles["intake-form__title"],
                    !registration &&
                      styles["intake-form__sub-title--edit-profile"],
                  )}
                >
                  About
                </div>
                <p
                  className={getClasses(
                    styles["intake-form__text"],
                    !registration && styles["intake-form__text-block"],
                  )}
                >
                  Describe your approach and your practice in a nutshell.
                </p>
                <Form.Group className={styles["about-me__bio"]}>
                  <Form.Control
                    ref={textareaRef}
                    disabled={gptBusy}
                    as="textarea"
                    rows={10}
                    name="biography"
                    placeholder="Type here..."
                    value={data.biography || ""}
                    maxLength={1000}
                    onChange={handleInputChange}
                    onFocus={() =>
                      track(amplitudePageName, "about-field", "focus")
                    }
                    style={{
                      paddingBottom: "2.5rem",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please write about yourself.
                  </Form.Control.Feedback>
                  <div className={styles["about-me__bio-suggest"]}>
                    <Button
                      disabled={gptBusy}
                      variant={"link"}
                      className={styles["about-me__bio-suggest-button"]}
                      onClick={() => {
                        getAIBio();
                      }}
                    >
                      <AIEmoji16 className={"mr-2"} /> Suggest SEO Optimized Bio
                    </Button>
                  </div>
                </Form.Group>
                <div
                  className={getClasses(
                    data.biography?.length > 1000 ? "red" : "gray01",
                    "d-flex justify-content-end rg-12",
                  )}
                >
                  ({data.biography?.length ?? 0}/1000 characters)
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
      {/* {!registration && <hr className={styles["intake-form__hr"]} />} */}
      {(!registration || (registration && tab === "name")) && (
        <div>
          <div className={styles["edit-profile__card"]}>
            {/* <div className={styles["intake-form__title"]}>
                1. Please provide details about you.
              </div> */}
            <div className={styles["intake-form__title"]}>Name</div>
            <p className={"rg-14 gray01"}>
              Your legal name set during registration is:{" "}
              <b className={"darker"}>{`${
                user?.legal_first_name || user?.first_name
              } ${user?.last_name}`}</b>
            </p>
            <hr className={styles["edit-profile__hr"]} />
            <div className={styles["edit-profile__col"]}>
              <p className={"rg-12 mb-2"}>
                How should Orchid communicate with you?
              </p>
              <Form.Group>
                <Form.Label className="rg-12 gray01 d-flex align-items-center">
                  Preferred First Name
                  <OverlayTrigger
                    placement="right"
                    trigger={["hover", "click"]}
                    overlay={
                      <Tooltip id="prefer-name-tooltip">
                        If different than your legal first name
                      </Tooltip>
                    }
                  >
                    <OutlineInfoCircle16 className={"blue ml-1"} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="preferred_first_name"
                  value={data.preferred_first_name || ""}
                  onChange={onInputChange}
                  placeholder={"Enter preferred first name"}
                  onFocus={() =>
                    track(amplitudePageName, "preferred-name-field", "focus")
                  }
                />
              </Form.Group>

              {/* need to change options based on medical degree */}
              <p className={"rg-12 mb-2"}>
                How do you want to be shown to your patients?
              </p>
              <Form.Group>
                <Form.Label className="rg-12 gray01 d-flex align-items-center">
                  Display Name
                  <OverlayTrigger
                    placement="right"
                    trigger={["hover", "click"]}
                    overlay={
                      <Tooltip id="display-name-tooltip">
                        This name will be shown on Messages, Bookings, Invites,
                        etc
                      </Tooltip>
                    }
                  >
                    <OutlineInfoCircle16 className={"blue ml-1"} />
                  </OverlayTrigger>
                </Form.Label>
                <CustomSelect
                  required
                  isMulti={false}
                  defaultValue={getDisplayNameOptions(
                    getOrchidProMedicalDegree(data),
                  ).find(
                    (option) =>
                      option.value ===
                      `First Name Last Name, ${getOrchidProMedicalDegree(
                        data,
                      )}`,
                  )}
                  value={
                    getDisplayNameOptions(getOrchidProMedicalDegree(data)).find(
                      (option) => option.value === data.name_schema,
                    ) || ""
                  }
                  onChange={(el) =>
                    fieldChangedCallback("name_schema", el.value)
                  }
                  options={getDisplayNameOptions(
                    getOrchidProMedicalDegree(data),
                  )}
                  onClick={() =>
                    track(
                      amplitudePageName,
                      "display-name-format-dropdown",
                      "click",
                    )
                  }
                />
              </Form.Group>

              {user && data.name_schema && (
                <div className={"rg-14 font-weight-bold"}>
                  Your Display Name:{" "}
                  <span className={"blue"}>
                    {getOrchidProDisplayName({
                      first_name: user.first_name,
                      last_name: user.last_name,
                      preferred_first_name: data.preferred_first_name,
                      name_schema: data.name_schema,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={styles["edit-profile__card"]}>
            <div className={styles["edit-profile__col"]}>
              <div className={getClasses(styles["intake-form__title"])}>
                Date of birth
              </div>
              <Form.Group>
                <Form.Label className="rg-12 gray01 mb-1">
                  Date of Birth <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  name="dob"
                  max={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 13),
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                  pattern="^(0[1-9]|1[012])/(0[1-9]|[12][0-9]|3[01])/(19|20)\d\d$"
                  placeholder="mm/dd/yyyy"
                  value={data.dob || ""}
                  onChange={onInputChange}
                  onFocus={() =>
                    track(amplitudePageName, "date-of-birth-field", "focus")
                  }
                />
              </Form.Group>
            </div>
            <hr className={styles["edit-profile__hr"]} />
            <div className={styles["edit-profile__col"]}>
              <div className={getClasses(styles["intake-form__title"])}>
                Language
              </div>
              <Form.Group>
                <Form.Label className="rg-12 gray01">Language</Form.Label>
                <DropdownWithSearch
                  fieldName={"languages"}
                  options={LANGUAGE_SPOKEN_OPTIONS}
                  data={data.languages || []}
                  fieldChangedCallback={fieldChangedCallback}
                  placeholder={"Select Languages"}
                  onFocus={() =>
                    track(amplitudePageName, "language-field", "focus")
                  }
                  onClick={() => null}
                />
              </Form.Group>
            </div>
            <hr className={styles["edit-profile__hr"]} />
            <div className={styles["edit-profile__col"]}>
              <div className={getClasses(styles["intake-form__title"])}>
                Year of Experience
              </div>
              <Form.Group>
                <Form.Label className="rg-12 gray01">
                  Years of Practice
                </Form.Label>
                <Form.Control
                  type="number"
                  name="years_of_practice"
                  value={data.years_of_practice || ""}
                  onChange={(e) => {
                    debouncedTrack(
                      amplitudePageName,
                      "years-of-experience-field",
                      "click",
                    );
                    onInputChange(e);
                  }}
                  placeholder={"Enter years"}
                  min={0}
                />
              </Form.Group>
            </div>
          </div>

          <div className={styles["edit-profile__card"]}>
            <div className={styles["edit-profile__col"]}>
              <div className={getClasses(styles["intake-form__title"])}>
                Additional Links
              </div>
              <p className={styles["intake-form__text"]}>
                If you have any additional links (website, social media, blog,
                etc) you want to share, please provide URL.
              </p>

              <Form.Label className="rg-12 gray01">URL</Form.Label>
              {getCurrLinks(data.links)?.map((link, index) => {
                return (
                  <Form.Group key={index}>
                    <Form.Control
                      type="text"
                      value={link || ""}
                      onChange={onLinkInputChange(index)}
                      onFocus={() => {
                        track(
                          amplitudePageName,
                          "additional-link-field",
                          "focus",
                        );
                      }}
                      placeholder={"Enter URL"}
                    />
                  </Form.Group>
                );
              })}
              <Button
                variant="link"
                className="d-flex align-items-center mb-3 p-0"
                onClick={(e) => {
                  track(amplitudePageName, "add-link-button", "click");
                  addLink(e);
                }}
              >
                <OutlinePlus16 className="mr-1" /> Add Link
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* {!registration && (
        <hr
          className={getClasses(
            styles["intake-form__hr"],
            styles["intake-form__hr--edit-profile"],
          )}
        />
      )} */}
    </>
  );
};

export default AboutMe;
